/**
 * InviteUserModal — Form for inviting new users to the organization.
 *
 * Fields: email (required), display name, job title, department, reports to, role.
 * Pre-selects department/manager if invoked from a department or tree context.
 */
import React, { useState, useEffect, useRef } from 'react';
import UserPlus from 'lucide-react/dist/esm/icons/user-plus';
import Mail from 'lucide-react/dist/esm/icons/mail';
import type { OrgUser, Department, ModuleRegistryItem } from '../../../services/api/org';
import { Button, Modal, TextField, Text } from '../../../components/ui';
import { Select } from '../../../components/ui';
import ModuleBadges from '../../../components/ui/ModuleBadges';

interface InviteUserModalProps {
  isOpen: boolean;
  onClose: () => void;
  users: OrgUser[];
  departments: Department[];
  onInvite: (data: {
    email: string;
    display_name?: string;
    title?: string;
    system_role?: string;
    department_id?: string;
    reports_to?: string;
    module_access?: string[];
    module_roles?: Record<string, string>;
  }) => Promise<OrgUser>;
  defaultDepartmentId?: string;
  defaultManagerId?: string;
  orgEnabledModules?: string[];
  moduleRegistry?: ModuleRegistryItem[];
}

const ROLES = [
  { value: 'user', label: 'User', desc: 'Standard access — can view their own data' },
  { value: 'org_admin', label: 'Org Admin', desc: 'Manages users, departments, and settings for this org' },
  { value: 'tenant_admin', label: 'Tenant Admin', desc: 'Full access across all organizations in the tenant' },
];

const InviteUserModal: React.FC<InviteUserModalProps> = ({
  isOpen,
  onClose,
  users,
  departments,
  onInvite,
  defaultDepartmentId,
  defaultManagerId,
  orgEnabledModules = [],
  moduleRegistry = [],
}) => {
  const emailRef = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [title, setTitle] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [managerId, setManagerId] = useState('');
  const [role, setRole] = useState('user');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [selectedModules, setSelectedModules] = useState<Set<string>>(new Set());
  const [moduleRoles, setModuleRoles] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setDisplayName('');
      setTitle('');
      setDepartmentId(defaultDepartmentId || '');
      setManagerId(defaultManagerId || '');
      setRole('user');
      setError(null);
      setSuccess(false);
      setSaving(false);
      setSelectedModules(new Set(orgEnabledModules));
      const defaultRoles: Record<string, string> = {};
      moduleRegistry.forEach((m) => {
        if (orgEnabledModules.includes(m.id)) {
          defaultRoles[m.id] = m.default_role;
        }
      });
      setModuleRoles(defaultRoles);
      setTimeout(() => emailRef.current?.focus(), 100);
    }
  }, [isOpen, defaultDepartmentId, defaultManagerId, orgEnabledModules, moduleRegistry]);

  const handleSubmit = async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError('Email is required');
      emailRef.current?.focus();
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setError('Invalid email address');
      emailRef.current?.focus();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const moduleAccessList = orgEnabledModules.length > 0
        ? Array.from(selectedModules)
        : undefined;
      const moduleRolesPayload = Object.keys(moduleRoles).length > 0
        ? Object.fromEntries(
            Object.entries(moduleRoles).filter(([m]) => selectedModules.has(m))
          )
        : undefined;

      await onInvite({
        email: trimmedEmail,
        display_name: displayName.trim() || undefined,
        title: title.trim() || undefined,
        system_role: role,
        department_id: departmentId || undefined,
        reports_to: managerId || undefined,
        module_access: moduleAccessList,
        module_roles: moduleRolesPayload,
      });
      setSuccess(true);
      // Auto-close after brief success flash
      setTimeout(() => onClose(), 800);
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to invite user');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <Modal.Header onClose={onClose}>
        <div className="w-8 h-8 rounded-lg bg-status-success/20 flex items-center justify-center">
          <UserPlus size={16} className="text-status-success" />
        </div>
        <h2 className="text-lg font-semibold text-content flex-1">Invite Team Member</h2>
      </Modal.Header>

      {/* Success state */}
      {success && (
        <div className="px-6 pb-6 pt-4">
          <div className="flex items-center gap-3 p-4 rounded-xl bg-status-success/10 border border-status-success/20">
            <Mail size={18} className="text-status-success shrink-0" />
            <div>
              <p className="text-sm font-medium text-status-success">Invitation sent!</p>
              <p className="text-xs text-status-success/60 mt-0.5">{email} will receive an invitation email.</p>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {!success && (
        <>
          <Modal.Body className="space-y-4">
            {/* Email */}
            <TextField
              ref={emailRef}
              label="Email Address"
              required
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="colleague@company.com"
              error={error || undefined}
              size="lg"
            />

            {/* Name + Title row */}
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Display Name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="John Doe"
              />
              <TextField
                label="Job Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Software Engineer"
              />
            </div>

            {/* Department */}
            <div>
              <Text variant="label" className="block mb-1.5">Department</Text>
              <Select
                value={departmentId}
                onChange={setDepartmentId}
                options={[
                  { value: '', label: 'Assign later' },
                  ...departments.map((d) => ({ value: d.id, label: d.name })),
                ]}
                placeholder="Assign later"
              />
            </div>

            {/* Reports To */}
            <div>
              <Text variant="label" className="block mb-1.5">Reports To</Text>
              <Select
                value={managerId}
                onChange={setManagerId}
                options={[
                  { value: '', label: 'Assign later' },
                  ...users
                    .filter((u) => u.status !== 'deactivated')
                    .map((u) => ({
                      value: u.id,
                      label: `${u.display_name || u.email.split('@')[0]}${u.title ? ` — ${u.title}` : ''}`,
                    })),
                ]}
                placeholder="Assign later"
              />
            </div>

            {/* Role */}
            <div>
              <Text variant="label" className="block mb-1.5">System Role</Text>
              <div className="space-y-1.5">
                {ROLES.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setRole(r.value)}
                    className={`
                      w-full flex items-start gap-3 p-3 rounded-xl text-left transition-all cursor-pointer border outline-none
                      ${role === r.value
                        ? 'bg-brand/10 border-brand/25'
                        : 'bg-transparent border-subtle hover:bg-surface-primary hover:border-subtle'
                      }
                    `}
                  >
                    <div className={`w-3.5 h-3.5 rounded-full border-2 mt-0.5 shrink-0 transition-colors
                      ${role === r.value ? 'border-brand bg-brand' : 'border-strong bg-transparent'}
                    `} />
                    <div>
                      <span className={`text-xs font-medium block ${role === r.value ? 'text-content' : 'text-content-secondary'}`}>
                        {r.label}
                      </span>
                      <Text variant="caption" className="block mt-0.5 !text-content-muted !text-[10px]">{r.desc}</Text>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Module Access */}
            {orgEnabledModules.length > 0 && (
              <div>
                <Text variant="label" className="block mb-2">Module Access</Text>
                <div className="space-y-1.5">
                  {moduleRegistry
                    .filter((m) => orgEnabledModules.includes(m.id))
                    .map((mod) => {
                      const isChecked = selectedModules.has(mod.id);
                      return (
                        <div key={mod.id} className="flex items-center justify-between p-2.5 rounded-lg bg-surface-page border border-subtle">
                          <div className="flex items-center gap-2">
                            <ModuleBadges modules={[mod.id]} size="sm" />
                            <span className="text-xs text-content">{mod.name}</span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => {
                                const next = new Set(selectedModules);
                                if (isChecked) next.delete(mod.id);
                                else next.add(mod.id);
                                setSelectedModules(next);
                              }}
                              className="sr-only"
                            />
                            <div className={`w-8 h-4 rounded-full transition-colors ${isChecked ? 'bg-status-success' : 'bg-surface-primary border border-default'}`}>
                              <div className={`w-3 h-3 rounded-full bg-white transition-all mt-0.5 ${isChecked ? 'ml-4' : 'ml-0.5'}`} />
                            </div>
                          </label>
                        </div>
                      );
                    })}
                </div>
              </div>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="ghost" size="lg" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="success"
              size="lg"
              loading={saving}
              disabled={!email.trim()}
              onClick={handleSubmit}
            >
              Send Invitation
            </Button>
          </Modal.Footer>
        </>
      )}
    </Modal>
  );
};

export default React.memo(InviteUserModal);
